import type { Call, CallingSessionIntent, Capability, CommandReceipt, CreateCall, Destination, EventEnvelope } from '@bcio-voice/contracts';
import type { CallControlApi, DeviceBootstrapSource, DeviceHeartbeat } from './api.js';
import { parseSipDeviceBootstrap, type BootstrapStatus, type CanonicalBootstrapDefaults, type ValidatedBootstrap } from './bootstrap.js';
import { MIN_CREDENTIAL_ROTATION_MS } from './credentials.js';
import {
  CAPTURE_CAPABILITY, DEFAULT_CAPTURE_LIMITS, LocalCaptureSession, captureCapabilityStatus, validCaptureConsent,
  type CaptureAvailability, type CaptureBackend, type CaptureLimits, type LocalCaptureConsent, type LocalCaptureHandle,
} from './capture.js';
import { CommandRunner, DEFAULT_COMMAND_TIMING, type CommandTiming, type CommandView } from './commands.js';
import { Emitter, SnapshotStore, type EventSource } from './events.js';
import { DTMF_PATTERN, defaultUuidFactory, isE164, isUuid, newIdempotencyKey, type UuidFactory } from './ids.js';
import { DEFAULT_AUDIO_PROCESSING, MediaController, stopStream, type AudioProcessing, type MicrophonePermission } from './media.js';
import { noLifecycle, type AudioSink, type DeviceLock, type DeviceLockHandle, type LifecyclePlatform, type MediaPlatform, type MediaStreamLike, type MediaStreamTrackLike } from './platform.js';
import { engineError, fail, ok, okVoid, type Disposer, type EngineError, type Result } from './result.js';
import { DEFAULT_RECONNECT_DELAYS_MS, TimerSet, backoffDelay, systemScheduler, withTimeout, type Scheduler } from './scheduler.js';
import { SipError, sipFailureOf, type SipFailure, type SipMediaState, type SipSession, type SipTerminationReason, type SipTransport, type SipTransportFactory } from './sip.js';
import { canTransitionLeg, derivePhase, legLive, terminalCall, type CallPhase, type LegState } from './state.js';
import { DiagnosticReporter, metricsFromStats } from './diagnostics.js';
import { TransportRecorder, type SipSocketObservation, type TransportLogEntry } from './transport-diagnostics.js';
import type { DiagnosticReportKind } from '@bcio-voice/contracts';
import type { CallSnapshot, CallTracks, EngineEvent, EngineLogger, EngineSnapshot, MediaPathState, MicrophoneState, OfferRefusalReason, PhoneIssue, PhoneSnapshot, PhoneStatus, TrackChangeCause } from './types.js';

export interface CallEnginePolicy {
  /** Exact SIP hostnames this build may use. The bootstrap payload cannot widen this list. */
  readonly allowedSipHosts: readonly string[];
  /** Development/test only: allow `ws://` to loopback. */
  readonly allowInsecureLoopbackTransport?: boolean;
  /** Live legs plus offers under verification. Default 2 (one call plus one waiting). */
  readonly maxConcurrentCalls?: number;
  /** Legacy opt-in gate. Default false: registration and ringing do not need audio capture. */
  readonly requireMicrophoneBeforeRegister?: boolean;
  /** Keep an idle muted microphone source for the app session (iPhone Safari). */
  readonly retainMicrophoneForSession?: boolean;
  /** `server` (default) sends the audited `dtmf` call action; `rtp` uses RFC 4733 from the browser. */
  readonly dtmfTransport?: 'server' | 'rtp';
  readonly heartbeatIntervalMs?: number;
  readonly offerVerificationTimeoutMs?: number;
  /** Bounded wait for the server end action before the local SIP reject/BYE, so the server records intent first. */
  readonly serverActionBeforeSipMs?: number;
  /** UX 6.2.12: audio lost this long ends the call. Default 15 s. */
  readonly mediaLossDropMs?: number;
  /** Attempt an in-dialog ICE restart on `disconnected`. Off by default: not verified against Asterisk. */
  readonly iceRestartOnDisconnect?: boolean;
  readonly reconnectDelaysMs?: readonly number[];
  readonly reconnectJitter?: number;
  /** After this many failed attempts the phone shows `offline`; slow retries continue at the last delay. */
  readonly offlineAfterAttempts?: number;
  readonly wakeGapMs?: number;
  readonly wakeCheckIntervalMs?: number;
  readonly endingConfirmTimeoutMs?: number;
  readonly retainEndedCalls?: number;
  readonly noAnswerTimeoutSeconds?: number;
  readonly createReconcileDelaysMs?: readonly number[];
}

export interface CallEngineOptions {
  /** Server device ID from `POST /api/v1/devices`. */
  readonly deviceId: string;
  /** When set, inbound offers must list this user as a participant of the verified call. */
  readonly userId?: string;
  readonly clientVersion: string;
  readonly api: CallControlApi;
  /** Post bounded media-health reports when `api.reportDiagnostics` exists (default true). */
  readonly diagnostics?: boolean;
  readonly bootstrapSource: DeviceBootstrapSource;
  /** Raw `bootstrap` field from the device creation response, if any. Validated like any other payload. */
  readonly initialBootstrap?: unknown;
  readonly sip: SipTransportFactory;
  readonly media: MediaPlatform;
  readonly lifecycle?: LifecyclePlatform;
  readonly deviceLock?: DeviceLock;
  readonly captureBackend?: CaptureBackend;
  readonly captureLimits?: CaptureLimits;
  /** Latest `GET /api/v1/capabilities` data, or null when unknown. PSTN is refused locally unless enabled. */
  readonly capabilities?: () => Readonly<Record<string, Capability>> | null;
  readonly scheduler?: Scheduler;
  readonly random?: () => number;
  readonly uuid?: UuidFactory;
  readonly logger?: EngineLogger;
  readonly audioProcessing?: AudioProcessing;
  readonly commandTiming?: CommandTiming;
  readonly userAgentString?: string;
  /**
   * Values the canonical `Device.bootstrap` does not carry: REGISTER expiry (default 120 s), ICE
   * servers (default none), and the INVITE correlation header names (default X-BCIO-Call-Id,
   * X-BCIO-Leg-Id, X-BCIO-Leg-Role). Ignored for proposed-v1 documents, which carry their own.
   */
  readonly canonicalBootstrap?: Partial<CanonicalBootstrapDefaults>;
  readonly policy: CallEnginePolicy;
}

export interface PlaceCallInput {
  readonly destination: Destination;
  readonly from_number_id?: string;
  readonly contact_id?: string;
  readonly calling_session?: CallingSessionIntent;
}

export interface PlacedCall {
  readonly key: string;
  /** Settles when `POST /calls` has a definitive result. `ok` means accepted for processing, not connected. */
  readonly request: Promise<Result<CommandView>>;
}

interface CallRecord {
  readonly key: string;
  readonly origin: 'outbound' | 'inbound';
  readonly createdAt: number;
  callId: string | null;
  server: Call | null;
  leg: LegState;
  sip: SipSession | null;
  sipDisposers: Disposer[];
  localStream: MediaStreamLike | null;
  trackDisposer: Disposer | null;
  sink: AudioSink | null;
  externalSink: boolean;
  remoteAudio: CallSnapshot['remote_audio'];
  muted: boolean;
  microphone: MicrophoneState;
  media: MediaPathState;
  signaling: 'live' | 'degraded';
  request: CreateCall | null;
  createKey: string | null;
  create: CommandView | null;
  createSettled: Promise<void> | null;
  commands: CommandView[];
  cancelRequested: boolean;
  wantBye: boolean;
  byeSent: boolean;
  localFailure: boolean;
  endReason: string | null;
  error: EngineError | null;
  acceptPromise: Promise<Result<void>> | null;
  endPromise: Promise<Result<void>> | null;
  dtmfChain: Promise<unknown>;
  lastPhase: CallPhase | null;
  timers: TimerSet;
  capture: LocalCaptureSession | null;
  recovering: boolean;
  refreshing: Promise<void> | null;
  /** Tracks last announced through `tracks_changed`; the remote one is watched for `ended`. */
  localTrack: MediaStreamTrackLike | null;
  remoteTrack: MediaStreamTrackLike | null;
  remoteTrackDisposer: Disposer | null;
  /** Diagnostics: whether media ever connected, the last media report kind, and whether teardown was reported. */
  diagConnected: boolean;
  diagMedia: DiagnosticReportKind | null;
  diagTeardown: boolean;
  /** SIP WebSocket reconnect attempts made while this call's signaling was degraded (diagnostics `reconnects`). */
  reconnects: number;
}

interface PendingOffer {
  readonly offer: SipSession;
  readonly callId: string;
  readonly role: 'source' | 'target';
  done: boolean;
}

const HEARTBEAT_STATE: Record<PhoneStatus, DeviceHeartbeat['registration_state']> = {
  idle: 'unregistered', starting: 'registering', blocked: 'failed', needs_microphone: 'unregistered', in_another_tab: 'unregistered',
  connecting: 'registering', registering: 'registering', registered: 'registered', reconnecting: 'registering', offline: 'failed',
  unregistered: 'unregistered', revoked: 'unregistered',
};

const MAX_COMMAND_HISTORY = 10;
const BOOTSTRAP_RELOAD_REASONS = new Set(['start', 'retry_now', 'bootstrap_retry', 'bootstrap_refresh', 'bootstrap_expired', 'visible', 'wake', 'online', 'backoff']);

/**
 * Browser/Electron call engine. One instance per signed-in device.
 *
 * - Calls start only through `placeCall` -> `POST /api/v1/calls`; the SIP side cannot originate.
 * - Incoming SIP offers must carry the server correlation headers, are verified with the API and
 *   refused (with an `offer_refused` event) otherwise.
 * - UI reads `getSnapshot()` / `subscribe()` (useSyncExternalStore) and `events`; all disposers are returned.
 */
export class CallEngine {
  readonly events: EventSource<EngineEvent>;
  private readonly emitter: Emitter<EngineEvent>;
  private readonly store: SnapshotStore<EngineSnapshot>;
  private readonly scheduler: Scheduler;
  private readonly uuid: UuidFactory;
  private readonly random: () => number;
  private readonly lifecycle: LifecyclePlatform;
  private readonly media: MediaController;
  private readonly commands: CommandRunner;
  private readonly timers: TimerSet;
  private readonly policy: Required<CallEnginePolicy>;
  private readonly diagnostics: DiagnosticReporter | null;
  private readonly deviceId: string;
  private readonly calls = new Map<string, CallRecord>();
  private readonly byCallId = new Map<string, string>();
  private readonly pendingOffers = new Set<PendingOffer>();
  private readonly lifecycleDisposers: Disposer[] = [];
  private readonly userAgentString: string;
  private readonly transportDiag: TransportRecorder;

  private revision = 0;
  private phone: PhoneSnapshot;
  private issues = new Set<PhoneIssue>();
  private bootstrap: ValidatedBootstrap | null = null;
  private bootstrapState: { status: BootstrapStatus; reason_code: string } | null = null;
  private initialBootstrap: unknown;
  private hasInitialBootstrap: boolean;
  private transport: SipTransport | null = null;
  private transportUrl: string | null = null;
  private generation = 0;
  private registered = false;
  private wasRegistered = false;
  private attempts = 0;
  private connectedAt: number | null = null;
  private shortDrops = 0;
  /** Unexpected WebSocket drops; a REGISTER failure that straddles one was already handled by it. */
  private transportDrops = 0;
  private bootstrapAttempts = 0;
  private running = false;
  private started: Promise<Result<void>> | null = null;
  private disposed = false;
  private revoked = false;
  private driving: Promise<void> | null = null;
  private driveAgain: string | null = null;
  private initializing = false;
  private lockHandle: DeviceLockHandle | null = null;
  private heartbeatInFlight = false;
  private heartbeatSettled: Promise<void> | null = null;
  private lastTick = 0;
  private mediaReady: Promise<void> | null = null;

  constructor(private readonly options: CallEngineOptions) {
    if (!isUuid(options.deviceId)) throw new TypeError('deviceId must be the server device UUID.');
    if (options.userId !== undefined && !isUuid(options.userId)) throw new TypeError('userId must be a UUID.');
    if (!options.policy.allowedSipHosts.length) throw new TypeError('policy.allowedSipHosts must list the PBX hostname(s).');
    if (!/^[\x20-\x7e]{1,80}$/.test(options.clientVersion)) throw new TypeError('clientVersion must be 1-80 printable characters.');
    this.deviceId = options.deviceId.toLowerCase();
    this.scheduler = options.scheduler ?? systemScheduler;
    this.uuid = options.uuid ?? defaultUuidFactory();
    this.random = options.random ?? Math.random;
    this.lifecycle = options.lifecycle ?? noLifecycle;
    this.timers = new TimerSet(this.scheduler);
    this.userAgentString = options.userAgentString ?? 'BCIOVoiceCallClient/0.1';
    const p = options.policy;
    this.policy = {
      allowedSipHosts: Object.freeze([...p.allowedSipHosts]),
      allowInsecureLoopbackTransport: p.allowInsecureLoopbackTransport ?? false,
      maxConcurrentCalls: Math.max(1, Math.min(p.maxConcurrentCalls ?? 2, 4)),
      requireMicrophoneBeforeRegister: p.requireMicrophoneBeforeRegister ?? false,
      retainMicrophoneForSession: p.retainMicrophoneForSession ?? false,
      dtmfTransport: p.dtmfTransport ?? 'server',
      heartbeatIntervalMs: p.heartbeatIntervalMs ?? 25_000,
      offerVerificationTimeoutMs: p.offerVerificationTimeoutMs ?? 4000,
      serverActionBeforeSipMs: p.serverActionBeforeSipMs ?? 2000,
      mediaLossDropMs: p.mediaLossDropMs ?? 15_000,
      iceRestartOnDisconnect: p.iceRestartOnDisconnect ?? false,
      reconnectDelaysMs: p.reconnectDelaysMs ?? DEFAULT_RECONNECT_DELAYS_MS,
      reconnectJitter: p.reconnectJitter ?? 0.2,
      offlineAfterAttempts: p.offlineAfterAttempts ?? 5,
      wakeGapMs: p.wakeGapMs ?? 30_000,
      wakeCheckIntervalMs: p.wakeCheckIntervalMs ?? 5000,
      endingConfirmTimeoutMs: p.endingConfirmTimeoutMs ?? 10_000,
      retainEndedCalls: p.retainEndedCalls ?? 10,
      noAnswerTimeoutSeconds: p.noAnswerTimeoutSeconds ?? 90,
      createReconcileDelaysMs: p.createReconcileDelaysMs ?? [2000, 5000, 10000, 20000],
    };
    this.initialBootstrap = options.initialBootstrap;
    this.hasInitialBootstrap = Object.hasOwn(options, 'initialBootstrap');
    const reportListenerError = (error: unknown) => this.log('warn', 'listener_error', { error: error instanceof Error ? error.name : 'unknown' });
    this.emitter = new Emitter<EngineEvent>(reportListenerError);
    this.events = this.emitter;
    this.store = new SnapshotStore(() => this.buildSnapshot(), reportListenerError);
    this.transportDiag = new TransportRecorder(() => this.scheduler.now());
    this.phone = this.phoneSnapshot('idle', null);
    this.media = new MediaController(options.media, options.audioProcessing ?? DEFAULT_AUDIO_PROCESSING, {
      changed: () => this.invalidate(),
      emit: event => this.emitter.emit({ type: 'media', event }),
      selectedInputRemoved: () => { for (const r of this.liveRecords()) if (r.localStream) void this.recoverMicrophone(r); },
      selectedOutputRemoved: () => { for (const r of this.calls.values()) if (r.sink) void r.sink.setOutput('').catch(() => {}); },
      permissionChanged: permission => this.onPermissionChanged(permission),
    }, this.policy.retainMicrophoneForSession);
    const post = options.api.reportDiagnostics?.bind(options.api);
    this.diagnostics = post && options.diagnostics !== false ? new DiagnosticReporter({ deviceId: options.deviceId, post: async (callId, report, signal) => { const r = await post(callId, report, signal); if (!r.ok) throw new Error('diagnostics_rejected'); } }) : null;
    this.commands = new CommandRunner(options.api, this.scheduler, this.uuid, {
      receipt: (callId, call) => { const r = this.recordByCallId(callId); if (r) this.applyServer(r, call); },
      refresh: callId => { const r = this.recordByCallId(callId); if (r) void this.refreshCall(r); },
      update: (callId, view) => { const r = this.recordByCallId(callId); if (r) this.applyCommand(r, view); },
    }, options.commandTiming ?? DEFAULT_COMMAND_TIMING);
  }

  // ---- Snapshot and subscription -------------------------------------------------------

  getSnapshot = (): EngineSnapshot => this.store.getSnapshot();
  subscribe = (listener: () => void): Disposer => this.store.subscribe(listener);

  getCall(key: string): CallSnapshot | null {
    return this.getSnapshot().calls.find(c => c.key === key) ?? null;
  }

  // ---- Phone lifecycle -----------------------------------------------------------------

  /** Loads the bootstrap, checks microphone permission and registers. Idempotent. */
  start(): Promise<Result<void>> {
    if (this.disposed) return Promise.resolve(fail('engine_disposed', 'The call engine was disposed.'));
    if (this.started) return this.started.then(r => { void this.drive('start'); return r; });
    this.running = true;
    this.setPhone('starting', null);
    this.started = (async () => {
      this.initializing = true;
      this.mediaReady ??= this.media.init();
      await this.mediaReady;
      this.initializing = false;
      if (this.disposed) return fail<void>('engine_disposed', 'The call engine was disposed.');
      this.attachLifecycle();
      await this.drive('start');
      this.scheduleHeartbeat(0);
      return okVoid;
    })();
    return this.started;
  }

  /** Stops registration. Calls already in progress continue until they end; new offers are refused. */
  async unregister(): Promise<void> {
    if (this.disposed) return;
    this.running = false;
    this.started = null;
    this.timers.clear('reconnect');
    this.timers.clear('bootstrap_refresh');
    this.timers.clear('bootstrap_retry');
    await this.teardownTransport(true);
    this.setPhone('unregistered', 'user_unregistered');
    this.timers.clear('heartbeat');
    await this.heartbeatSettled;
    await this.sendHeartbeat();
    this.releaseLock();
    if (!this.liveRecords().length) this.media.releaseRetained();
  }

  /** UX "Retry now": skips the current backoff wait. */
  retryNow(): void {
    if (!this.running || this.disposed) return;
    this.timers.clear('reconnect');
    this.timers.clear('bootstrap_retry');
    this.attempts = 0;
    this.shortDrops = 0;
    this.bootstrapAttempts = 0;
    void this.drive('retry_now');
  }

  /** Retry a released lock. A live owner must explicitly hand off; never steal its calls. */
  takeOverPhone(): void {
    if (!this.running || this.disposed) return;
    void this.drive('take_over');
  }

  /** Call from a user gesture. On iPhone, hold a muted source until this phone session ends. */
  async requestMicrophonePermission(): Promise<Result<MicrophonePermission>> {
    if (this.disposed) return fail('engine_disposed', 'The call engine was disposed.');
    const result = await this.media.requestPermission();
    if (result.ok && this.running) void this.drive('permission');
    return result;
  }

  refreshDevices(): Promise<void> { return this.media.refreshDevices(); }

  async selectInput(deviceId: string | null): Promise<Result<void>> {
    const selected = this.media.selectInput(deviceId);
    if (!selected.ok) return selected;
    for (const record of this.liveRecords()) {
      if (!record.localStream) continue;
      const acquired = await this.media.acquire(deviceId);
      if (!acquired.ok) return { ok: false, error: acquired.error };
      if (!legLive(record.leg)) { stopStream(acquired.value.stream); continue; }
      const swapped = await this.swapLocalStream(record, acquired.value.stream);
      if (!swapped.ok) return swapped;
    }
    return okVoid;
  }

  async selectOutput(deviceId: string | null): Promise<Result<void>> {
    const selected = this.media.selectOutput(deviceId);
    if (!selected.ok) return selected;
    for (const record of this.calls.values()) {
      if (!record.sink) continue;
      try { await record.sink.setOutput(deviceId ?? ''); } catch { return fail('output_device_unavailable', 'The selected speaker could not be used.', true); }
    }
    return okVoid;
  }

  /** Retry remote audio after an autoplay block. Call from a user gesture. */
  async resumeAudioPlayback(): Promise<void> {
    for (const record of this.calls.values()) if (record.sink && record.remoteAudio === 'blocked') await this.play(record);
  }

  // ---- Calls ---------------------------------------------------------------------------

  /**
   * Requests a call through `POST /api/v1/calls`. The microphone is opened first (inside the user
   * gesture) so a denied microphone never creates a server call. The server then attaches this
   * device's leg; only an offer correlated to this exact call is auto-accepted.
   */
  async placeCall(input: PlaceCallInput): Promise<Result<PlacedCall>> {
    if (this.disposed) return fail('engine_disposed', 'The call engine was disposed.');
    if (!this.running) return fail('engine_not_started', 'Start the phone first.');
    const body = this.validateCreate(input);
    if (!body.ok) return body;
    const gate = this.capabilityGate(body.value.destination);
    if (!gate.ok) return gate;
    if (!this.acceptingOffers()) {
      await withTimeout(this.scheduler, this.drive('retry_now'), 8000, undefined);
      if (!this.acceptingOffers()) return fail('phone_not_ready', 'Your phone is reconnecting. Try again in a moment.', true);
    }
    if (this.liveRecords().length > 0 || this.pendingOffers.size > 0) return fail('busy', 'Finish the current call before starting another.');
    const mic = await this.media.acquire();
    if (!mic.ok) return { ok: false, error: mic.error };
    if (this.disposed || !this.running) { stopStream(mic.value.stream); return fail('engine_disposed', 'The call engine stopped.'); }
    if (this.liveRecords().length > 0) { stopStream(mic.value.stream); return fail('busy', 'Finish the current call before starting another.'); }
    const record = this.newRecord('outbound');
    record.request = body.value;
    record.createKey = body.value.calling_session?.attempt_id
      ? `attempt-${body.value.calling_session.attempt_id}`
      : newIdempotencyKey('call', this.uuid);
    this.setLocalStream(record, mic.value.stream);
    this.calls.set(record.key, record);
    this.commit(record);
    let settle!: (result: Result<CommandView>) => void;
    const request = new Promise<Result<CommandView>>(resolve => { settle = resolve; });
    record.createSettled = this.sendCreate(record).then(settle, () => settle(fail('server_unreachable', 'The call request failed.', true)));
    return ok({ key: record.key, request });
  }

  /** Answers an inbound offer after acquiring the microphone. Double activation returns the same promise. */
  accept(key: string, options: { readonly holdOthers?: boolean } = {}): Promise<Result<void>> {
    const record = this.calls.get(key);
    if (!record) return Promise.resolve(fail('call_not_found', 'That call is no longer available.'));
    if (record.acceptPromise) return record.acceptPromise;
    if (record.origin !== 'inbound' || record.leg !== 'offered') return Promise.resolve(fail('invalid_call_state', 'This call cannot be answered now.'));
    const promise = this.acceptInbound(record, options.holdOthers === true);
    record.acceptPromise = promise;
    void promise.finally(() => { if (record.acceptPromise === promise) record.acceptPromise = null; });
    return promise;
  }

  /** Declines an inbound offer: server `reject` (or `hangup`) action first, then SIP 603. */
  reject(key: string): Promise<Result<void>> {
    const record = this.calls.get(key);
    if (!record) return Promise.resolve(fail('call_not_found', 'That call is no longer available.'));
    if (record.endPromise) return record.endPromise;
    if (record.origin === 'outbound') return this.hangup(key);
    if (record.leg === 'terminating' || record.leg === 'terminated') return Promise.resolve(okVoid);
    if (record.leg !== 'offered') return Promise.resolve(fail('invalid_call_state', 'Use hang up for a connected call.'));
    record.endPromise = this.declineOffer(record);
    return record.endPromise;
  }

  /** Ends or cancels a call in any phase. Idempotent. */
  hangup(key: string): Promise<Result<void>> {
    const record = this.calls.get(key);
    if (!record) return Promise.resolve(fail('call_not_found', 'That call is no longer available.'));
    if (record.endPromise) return record.endPromise;
    if (record.origin === 'inbound' && record.leg === 'offered') return this.reject(key);
    if (record.leg === 'terminated') return Promise.resolve(okVoid);
    record.endPromise = this.endCall(record, 'local_hangup');
    return record.endPromise;
  }

  /** Local microphone mute. No server request; does not hold the call. */
  setMuted(key: string, muted: boolean): Result<void> {
    const record = this.calls.get(key);
    if (!record) return fail('call_not_found', 'That call is no longer available.');
    if (!record.localStream || !legLive(record.leg)) return fail('invalid_call_state', 'There is no microphone on this call.');
    record.muted = muted;
    this.applyMute(record);
    record.capture?.mark('muted', muted);
    this.commit(record);
    return okVoid;
  }

  /** Network hold (server action with expected_version and an idempotency key). */
  hold(key: string): Promise<Result<CommandView>> { return this.serverAction(key, 'hold'); }
  resume(key: string): Promise<Result<CommandView>> { return this.serverAction(key, 'resume'); }

  /** Sends DTMF in order. Default path is the audited server `dtmf` action. */
  sendDtmf(key: string, digits: string): Promise<Result<CommandView | null>> {
    const record = this.calls.get(key);
    if (!record) return Promise.resolve(fail('call_not_found', 'That call is no longer available.'));
    if (typeof digits !== 'string' || !DTMF_PATTERN.test(digits)) return Promise.resolve(fail('invalid_digits', 'Digits must be 0-9, *, # or A-D (1 to 16).'));
    if (record.leg !== 'established') return Promise.resolve(fail('invalid_call_state', 'The call is not connected.'));
    if (this.policy.dtmfTransport === 'rtp') {
      return Promise.resolve(record.sip?.sendRtpDtmf(digits) ? ok(null) : fail('invalid_call_state', 'Tones could not be sent on this call.', true));
    }
    const run = record.dtmfChain.then(async (): Promise<Result<CommandView | null>> => {
      const server = record.server;
      if (!record.callId || !server || !server.allowed_actions.includes('dtmf')) return fail('action_not_allowed', 'Tones are not allowed on this call now.');
      const view = await this.commands.run(record.callId, { action: 'dtmf', expected_version: server.version, digits }, null);
      return this.viewResult(view);
    });
    record.dtmfChain = run.catch(() => {});
    return run;
  }

  /**
   * The call's current microphone and remote audio tracks (also announced as `tracks_changed`).
   * Read-only access for local capture: never stop or disable them; remote audio keeps playing
   * through the engine's sink.
   */
  getCallTracks(key: string): CallTracks | null {
    const record = this.calls.get(key);
    return record ? { local: record.localTrack, remote: record.remoteTrack } : null;
  }

  /** Remote audio stream for custom rendering (meters, visualizers). */
  getRemoteStream(key: string): MediaStreamLike | null {
    return this.calls.get(key)?.sip?.remoteStream() ?? null;
  }

  /** Route this call's remote audio to a caller-owned sink instead of the engine's internal one. */
  attachRemoteAudio(key: string, sink: AudioSink): Result<void> {
    const record = this.calls.get(key);
    if (!record) return fail('call_not_found', 'That call is no longer available.');
    if (!legLive(record.leg)) return fail('invalid_call_state', 'The call has ended.');
    if (record.sink && !record.externalSink) { record.sink.setStream(null); record.sink.dispose(); }
    record.sink = sink;
    record.externalSink = true;
    if (record.leg === 'established') this.connectRemoteAudio(record);
    return okVoid;
  }

  /** Removes an ended call from the snapshot. */
  dismiss(key: string): Result<void> {
    const record = this.calls.get(key);
    if (!record) return fail('call_not_found', 'That call is no longer available.');
    if (legLive(record.leg) && !(record.leg === 'none' && record.localFailure)) return fail('invalid_call_state', 'End the call first.');
    this.removeRecord(record);
    return okVoid;
  }

  /** Feed authoritative calls from `GET /calls/{id}` or the event stream's refetch. Older versions are ignored. */
  applyServerCall(call: Call): void {
    const record = this.recordByCallId(call.id);
    if (record) this.applyServer(record, call);
  }

  /** Feed SSE envelopes; a newer call version for a tracked call triggers one coalesced refetch. */
  notifyServerEvent(envelope: Pick<EventEnvelope, 'aggregate'>): void {
    if (envelope.aggregate.type !== 'call') return;
    const record = this.recordByCallId(envelope.aggregate.id);
    if (record && (!record.server || envelope.aggregate.version > record.server.version)) void this.refreshCall(record);
  }

  readonly capture = {
    availability: (key: string): CaptureAvailability => {
      const record = this.calls.get(key);
      return record ? this.captureAvailability(record) : { status: 'unavailable', reason: 'call_not_found' };
    },
    start: (key: string, consent: LocalCaptureConsent): Promise<Result<LocalCaptureHandle>> => this.startCapture(key, consent),
  };

  /** Ends every call, unregisters, stops all tracks and timers and removes all listeners. */
  async dispose(): Promise<void> {
    if (this.disposed) return;
    const live = [...this.liveRecords()];
    await Promise.all(live.map(r => withTimeout(this.scheduler, this.hangup(r.key), this.policy.serverActionBeforeSipMs + 1000, null)));
    this.disposed = true;
    this.running = false;
    for (const pending of [...this.pendingOffers]) this.refuse(pending, 480, 'phone_not_ready');
    await this.teardownTransport(false);
    for (const record of [...this.calls.values()]) { this.cleanupMedia(record); record.timers.clearAll(); }
    this.calls.clear();
    this.byCallId.clear();
    this.commands.dispose();
    this.timers.clearAll();
    this.media.dispose();
    for (const dispose of this.lifecycleDisposers.splice(0)) { try { dispose(); } catch { /* ignore */ } }
    this.releaseLock();
    this.store.invalidate();
    this.store.clear();
    this.emitter.clear();
  }

  // ---- Registration driver --------------------------------------------------------------

  private drive(reason: string): Promise<void> {
    if (this.driving) { this.driveAgain = reason; return this.driving; }
    this.driving = (async () => {
      let next: string | null = reason;
      try {
        while (next !== null && this.running && !this.disposed) {
          this.driveAgain = null;
          await this.driveOnce(next);
          next = this.driveAgain;
        }
      } catch (error) {
        this.log('warn', 'drive_failed', { reason, error: error instanceof Error ? error.name.slice(0, 40) : 'unknown' });
        // Never stall silently: retry on the normal backoff with a visible reason.
        if (this.running && !this.disposed && !this.revoked && !this.registered && !this.timers.has('reconnect')) this.scheduleReconnect({ code: 'engine_error' });
      } finally {
        this.driving = null;
      }
    })();
    return this.driving;
  }

  private async driveOnce(reason: string): Promise<void> {
    if (!this.running || this.disposed || this.revoked) return;
    const platform = this.options.media;
    if (!platform.secureContext) { this.block('insecure_context'); return; }
    if (!platform.webrtcSupported) { this.block('webrtc_unsupported'); return; }

    if (this.options.deviceLock && !this.lockHandle) {
      const handle = await this.options.deviceLock.acquire({ steal: false });
      if (!this.running || this.disposed) { handle?.release(); return; }
      if (!handle) { this.setPhone('in_another_tab', 'phone_active_in_another_tab'); return; }
      this.adoptLock(handle);
    }

    // A refused bootstrap is re-fetched only on explicit triggers, not on every internal re-evaluation.
    const reload = this.bootstrapState === null || BOOTSTRAP_RELOAD_REASONS.has(reason);
    if ((!this.bootstrap && reload) || (this.bootstrap && this.scheduler.now() >= this.bootstrap.refreshAt)) {
      await this.loadBootstrap();
      if (!this.running || this.disposed || this.revoked) return;
    }
    if (!this.bootstrap || this.scheduler.now() >= this.bootstrap.expiresAt) {
      if (this.bootstrap) this.expireBootstrapNow();
      return;
    }

    if (this.policy.requireMicrophoneBeforeRegister && this.media.currentPermission !== 'granted') {
      // Unregister so the PBX stops offering calls; a live dialog is kept (teardown is deferred).
      if (this.transport && this.registered) await this.teardownTransport(true);
      this.setPhone('needs_microphone', this.media.currentPermission === 'denied' ? 'microphone_denied' : 'microphone_permission_required');
      return;
    }
    if (!this.lifecycle.isOnline()) {
      this.registered = false;
      this.setPhone('offline', 'network_offline');
      return;
    }
    if (this.timers.has('reconnect') && reason !== 'retry_now' && reason !== 'online') return;
    this.timers.clear('reconnect');

    const bootstrap = this.bootstrap;
    if (this.transport && this.transportUrl !== bootstrap.signalingUrl && this.liveRecords().length === 0) await this.teardownTransport(false);
    let transport = this.transport;
    let fresh = false;
    if (!transport) {
      const generation = ++this.generation;
      const { username, password } = bootstrap.credentials();
      try {
        transport = this.createTransport(generation, bootstrap, username, password);
      } catch (error) {
        // An adapter that refuses the configuration would otherwise leave the phone stuck with no retry.
        this.noteTransport(this.transportDiag.begin(bootstrap.signalingUrl, this.attempts + 1, 'connect'));
        this.scheduleReconnect(error instanceof SipError ? error.failure : { code: 'transport_create_failed' });
        return;
      }
      this.transport = transport;
      this.transportUrl = bootstrap.signalingUrl;
      fresh = true;
    }
    const generation = this.generation;

    let connectedNow = false;
    if (!transport.connected) {
      this.registered = false;
      this.setPhone('connecting', null);
      for (const record of this.liveRecords()) if (record.signaling === 'degraded') record.reconnects += 1;
      this.noteTransport(this.transportDiag.begin(bootstrap.signalingUrl, this.attempts + 1, 'connect'));
      try {
        if (fresh) await transport.connect(); else await transport.reconnect();
      } catch (error) {
        if (generation === this.generation && this.running) this.scheduleReconnect(sipFailureOf(error));
        return;
      }
      if (generation !== this.generation || !this.running) return;
      connectedNow = true;
    }

    if (!this.registered || reason === 'wake') {
      this.setPhone('registering', null);
      if (!connectedNow) this.noteTransport(this.transportDiag.begin(bootstrap.signalingUrl, this.attempts + 1, 'register'));
      const drops = this.transportDrops;
      try {
        await transport.register();
      } catch (error) {
        if (generation !== this.generation || !this.running) return;
        // The socket dropped while REGISTER was in flight: that drop already counted the failure and
        // scheduled the retry, so counting it again would skip a backoff step.
        if (this.transportDrops !== drops) return;
        const failure = sipFailureOf(error);
        // Credentials rejected. Fresh credentials are usually just not provisioned on the PBX yet
        // (B2 loads digests about once a second), so retry them; re-issue only once they are old
        // enough, because every issue rotates the secret and would restart provisioning.
        if ((failure.statusCode === 401 || failure.statusCode === 403) && this.scheduler.now() - bootstrap.receivedAt >= MIN_CREDENTIAL_ROTATION_MS) {
          this.bootstrap = null;
          this.timers.clear('bootstrap_refresh');
          this.timers.clear('bootstrap_expiry');
        }
        this.scheduleReconnect(failure);
        return;
      }
      if (generation !== this.generation || !this.running) return;
      this.registered = true;
      this.attempts = 0;
      this.connectedAt ??= this.scheduler.now();
      this.noteTransport(this.transportDiag.registered());
      this.issues.delete('signaling_degraded');
      for (const record of this.liveRecords()) if (record.signaling !== 'live') { record.signaling = 'live'; void this.diagnose(record, 'signaling_restored'); this.commit(record); }
      const resync = this.wasRegistered;
      this.wasRegistered = true;
      this.setPhone('registered', null, { registered_at: new Date(this.scheduler.now()).toISOString() });
      if (resync) this.emitter.emit({ type: 'resync_required', reason: 'reconnected' });
    } else if (this.phone.status !== 'registered') {
      this.setPhone('registered', null);
    }
  }

  private createTransport(generation: number, bootstrap: ValidatedBootstrap, username: string, password: string): SipTransport {
    return this.options.sip.create({
      ...(bootstrap.transport === 'https_long_poll' ? {httpSignalingUrl:bootstrap.signalingUrl} : {websocketUrl:bootstrap.signalingUrl}),
      aor: bootstrap.aor,
      username,
      password,
      registerExpiresSeconds: bootstrap.registerExpiresSeconds,
      iceServers: bootstrap.iceServers,
      iceTransportPolicy: bootstrap.iceTransportPolicy,
      userAgentString: this.userAgentString,
      noAnswerTimeoutSeconds: this.policy.noAnswerTimeoutSeconds,
    }, {
      onTransportState: (state, error) => this.onTransportState(generation, state, error),
      onRegistrationLost: error => this.onRegistrationLost(generation, error),
      onOffer: offer => this.onOffer(generation, offer),
      onTransportObservation: observation => this.onTransportObservation(generation, observation),
    });
  }

  private onTransportObservation(generation: number, observation: SipSocketObservation): void {
    // Sockets of a transport already torn down (generation bumped) are not this phone's story any more.
    if (generation !== this.generation || this.disposed) return;
    this.noteTransport(this.transportDiag.observe(observation));
  }

  /** Publishes one transport diagnostic change: snapshot, `transport_diagnostic` event and the debug log. */
  private noteTransport(entry: TransportLogEntry | null): void {
    if (!entry || this.disposed) return;
    this.phone = this.phoneSnapshot(this.phone.status, this.phone.reason, this.phone);
    this.store.invalidate();
    const transport = this.transportDiag.snapshot();
    this.emitter.emit({ type: 'transport_diagnostic', event: entry, transport });
    this.log('debug', 'transport', {
      kind: entry.kind, attempt: entry.attempt, failure: entry.failure, close_code: entry.close?.code ?? null, was_clean: entry.close?.was_clean ?? null,
      sip_status: entry.sip_status, connect_ms: entry.connect_ms, retry_in_ms: entry.retry_in_ms,
    });
  }

  private async loadBootstrap(): Promise<void> {
    let raw: unknown;
    if (this.hasInitialBootstrap) {
      raw = this.initialBootstrap;
      this.hasInitialBootstrap = false;
      this.initialBootstrap = undefined;
    } else {
      const loaded = await this.options.bootstrapSource.load(this.deviceId);
      if (this.disposed) return;
      if (!loaded.ok) {
        // B1 answers 404 for a revoked device or one bound to another session: stop for good.
        if (loaded.kind === 'http' && loaded.status === 404) { this.setBootstrapState('revoked', 'device_not_found'); await this.revoke('device_revoked'); return; }
        if (loaded.kind === 'http' && loaded.status === 401) this.issues.add('session_expired');
        if (this.bootstrap && this.scheduler.now() < this.bootstrap.expiresAt) {
          this.issues.add('bootstrap_refresh_failing');
          this.scheduleBootstrapRetry();
          this.invalidate();
          return;
        }
        const expired = this.phone.reason === 'bootstrap_expired';
        this.bootstrap = null;
        if (!expired) this.setBootstrapState('unavailable', loaded.kind === 'http' ? loaded.code : loaded.kind);
        this.block(expired ? 'bootstrap_expired' : 'bootstrap_unavailable');
        this.scheduleBootstrapRetry();
        return;
      }
      raw = loaded.value;
    }
    const parsed = parseSipDeviceBootstrap(raw, {
      deviceId: this.deviceId,
      now: this.scheduler.now(),
      allowedSipHosts: this.policy.allowedSipHosts,
      allowInsecureLoopback: this.policy.allowInsecureLoopbackTransport,
      random: this.random,
      ...(this.options.canonicalBootstrap ? { canonicalDefaults: this.options.canonicalBootstrap } : {}),
    });
    if (!parsed.ok) {
      this.setBootstrapState(parsed.status, parsed.reason_code);
      if (parsed.status === 'revoked') { await this.revoke('bootstrap_revoked'); return; }
      this.bootstrap = null;
      this.timers.clear('bootstrap_refresh');
      this.timers.clear('bootstrap_expiry');
      await this.teardownTransport(true);
      this.block(`bootstrap_${parsed.status}`);
      if (parsed.status === 'expired') this.scheduleBootstrapRetry();
      this.log('warn', 'bootstrap_refused', { status: parsed.status, reason: parsed.reason_code });
      return;
    }
    const next = parsed.bootstrap;
    this.bootstrap = next;
    this.bootstrapState = null;
    this.bootstrapAttempts = 0;
    this.issues.delete('bootstrap_refresh_failing');
    this.timers.clear('bootstrap_retry');
    // Same PBX: rotate digest credentials in place so established dialogs survive.
    if (this.transport && this.transportUrl === next.signalingUrl) {
      this.transport.updateCredentials(next.credentials());
      // Complete renewal with an actual PBX REGISTER on this UA, including during live calls.
      this.registered = false;
    }
    this.timers.set('bootstrap_refresh', next.refreshAt - this.scheduler.now(), () => { void this.drive('bootstrap_refresh'); });
    this.timers.set('bootstrap_expiry', next.expiresAt - this.scheduler.now(), () => this.expireBootstrapNow());
    this.invalidate();
  }

  private expireBootstrapNow(): void {
    if (!this.bootstrap || this.scheduler.now() < this.bootstrap.expiresAt) return;
    this.bootstrap = null;
    this.setBootstrapState('expired', 'bootstrap_expired');
    this.block('bootstrap_expired');
    void this.teardownTransport(true).then(() => { if (this.running) void this.drive('bootstrap_expired'); });
  }

  private scheduleBootstrapRetry(): void {
    if (this.timers.has('bootstrap_retry') || !this.running) return;
    this.bootstrapAttempts += 1;
    const wait = backoffDelay(this.bootstrapAttempts, this.policy.reconnectDelaysMs, this.policy.reconnectJitter, this.random);
    this.timers.set('bootstrap_retry', wait, () => { void this.drive('bootstrap_retry'); });
  }

  /**
   * Reconnect policy (UX 7.1): bounded exponential backoff, by default 1, 2, 4, 8, 15 and then 30 s,
   * each +/-20% jitter. After `offlineAfterAttempts` failures the phone shows `offline`
   * (`retries_exhausted`) but keeps retrying every 30 s; it never gives up on its own. The cause of
   * the last failure stays in `phone.transport.last_failure`. "Retry now" and coming back online
   * retry at once and restart the schedule; waking from sleep retries at once.
   */
  private scheduleReconnect(failure: SipFailure): void {
    this.registered = false;
    this.attempts += 1;
    this.noteTransport(this.transportDiag.fail(failure));
    const wait = backoffDelay(this.attempts, this.policy.reconnectDelaysMs, this.policy.reconnectJitter, this.random);
    const offline = this.attempts > this.policy.offlineAfterAttempts;
    const next = new Date(this.scheduler.now() + wait).toISOString();
    this.noteTransport(this.transportDiag.retry(wait));
    this.setPhone(offline ? 'offline' : 'reconnecting', offline ? 'retries_exhausted' : failure.code, { next_retry_at: next });
    this.timers.set('reconnect', wait, () => { void this.drive('backoff'); });
  }

  private onTransportState(generation: number, state: 'connected' | 'disconnected', error?: SipFailure): void {
    if (generation !== this.generation || this.disposed) return;
    if (state === 'connected') return;
    // Brief successful registrations must not reset a flapping phone to a one-second reopen forever.
    if (this.connectedAt !== null) {
      this.shortDrops = this.scheduler.now() - this.connectedAt < 60_000 ? this.shortDrops + 1 : 1;
      this.attempts = Math.max(this.attempts, this.shortDrops - 1);
      this.connectedAt = null;
    }
    this.transportDrops += 1;
    this.registered = false;
    for (const record of this.liveRecords()) if (record.leg !== 'none' && record.signaling !== 'degraded') { record.signaling = 'degraded'; void this.diagnose(record, 'signaling_degraded'); this.commit(record); }
    if (this.liveRecords().some(r => r.leg === 'established')) this.issues.add('signaling_degraded');
    if (!this.running) return;
    this.scheduleReconnect(error ?? { code: 'transport_closed' });
  }

  private onRegistrationLost(generation: number, error?: SipFailure): void {
    if (generation !== this.generation || this.disposed || !this.running) return;
    this.registered = false;
    this.log('warn', 'registration_lost', { code: error?.code ?? 'unknown' });
    if (this.timers.has('reconnect')) return;
    void this.drive('registration_lost');
  }

  private async teardownTransport(keepCalls: boolean): Promise<void> {
    const transport = this.transport;
    this.registered = false;
    if (!transport) return;
    if (keepCalls && this.liveRecords().some(r => r.sip)) {
      this.issues.add('teardown_deferred_for_active_call');
      try { await withTimeout(this.scheduler, transport.unregister(), 3000, null); } catch { /* best effort */ }
      this.invalidate();
      return;
    }
    this.transport = null;
    this.transportUrl = null;
    this.generation += 1;
    this.issues.delete('teardown_deferred_for_active_call');
    try { await withTimeout(this.scheduler, transport.unregister(), 3000, null); } catch { /* best effort */ }
    try { await withTimeout(this.scheduler, transport.disconnect(), 3000, null); } catch { /* best effort */ }
  }

  private maybeFinishDeferredTeardown(): void {
    if (!this.issues.has('teardown_deferred_for_active_call') || this.liveRecords().some(r => r.sip)) return;
    if (this.running && this.bootstrap && this.scheduler.now() < this.bootstrap.expiresAt && this.media.currentPermission === 'granted') {
      this.issues.delete('teardown_deferred_for_active_call');
      void this.drive('deferred_teardown');
      return;
    }
    void this.teardownTransport(false).then(() => this.invalidate());
  }

  private async revoke(reason: string): Promise<void> {
    if (this.revoked) return;
    this.revoked = true;
    this.bootstrap = null;
    this.setPhone('revoked', reason);
    this.timers.clear('reconnect');
    this.timers.clear('bootstrap_refresh');
    this.timers.clear('bootstrap_expiry');
    this.timers.clear('bootstrap_retry');
    for (const pending of [...this.pendingOffers]) this.refuse(pending, 480, 'phone_not_ready');
    await Promise.all(this.liveRecords().map(record => this.terminateLocally(record, 'device_revoked')));
    await this.teardownTransport(false);
    this.releaseLock();
    this.media.releaseRetained();
  }

  private block(reason: string): void {
    this.registered = false;
    this.setPhone('blocked', reason);
  }

  private onPermissionChanged(permission: MicrophonePermission): void {
    if (!this.running || this.disposed || this.initializing) return;
    if (permission === 'denied') {
      for (const record of this.liveRecords()) {
        const track = record.localStream?.getAudioTracks()[0];
        if (track && track.readyState !== 'live') { record.microphone = 'lost'; this.commit(record); }
      }
    }
    void this.drive('permission');
  }

  private adoptLock(handle: DeviceLockHandle): void {
    this.lockHandle = handle;
    this.scheduleHeartbeat(0);
    void handle.lost.then(() => {
      if (this.lockHandle !== handle || this.disposed) return;
      this.lockHandle = null;
      this.timers.clear('heartbeat');
      void this.teardownTransport(true).then(() => { if (this.running) this.setPhone('in_another_tab', 'phone_taken_by_another_tab'); });
    });
  }

  private releaseLock(): void {
    const handle = this.lockHandle;
    this.lockHandle = null;
    try { handle?.release(); } catch { /* ignore */ }
  }

  private attachLifecycle(): void {
    if (this.lifecycleDisposers.length) return;
    this.lifecycleDisposers.push(
      this.lifecycle.onOnline(() => { this.timers.clear('reconnect'); this.attempts = 0; void this.drive('online'); }),
      this.lifecycle.onOffline(() => { this.registered = false; if (this.running) this.setPhone('offline', 'network_offline'); }),
      this.lifecycle.onVisible(() => this.checkWake('visible')),
    );
    this.lastTick = this.scheduler.now();
    this.scheduleWakeTick();
  }

  private scheduleWakeTick(): void {
    this.timers.set('wake', this.policy.wakeCheckIntervalMs, () => { this.checkWake('tick'); if (!this.disposed) this.scheduleWakeTick(); });
  }

  /** UX 7.1: a timer gap over 30 s (sleep/wake) forces re-registration and a stream resync. */
  private checkWake(source: 'tick' | 'visible'): void {
    const now = this.scheduler.now();
    const gap = now - this.lastTick;
    this.lastTick = now;
    if (!this.running || this.disposed) return;
    if (gap > this.policy.wakeGapMs) {
      this.emitter.emit({ type: 'resync_required', reason: 'wake' });
      this.timers.clear('reconnect');
      void this.drive('wake');
    } else if (source === 'visible') {
      void this.drive('visible');
    }
  }

  // ---- Heartbeat --------------------------------------------------------------------------

  private scheduleHeartbeat(ms: number): void {
    if (this.disposed) return;
    this.timers.set('heartbeat', ms, () => { void this.sendHeartbeat(); });
  }

  private sendHeartbeat(): Promise<void> {
    if (this.disposed || this.revoked || this.heartbeatInFlight || (this.options.deviceLock && !this.lockHandle)) return Promise.resolve();
    this.heartbeatSettled = this.postHeartbeat();
    return this.heartbeatSettled;
  }

  private async postHeartbeat(): Promise<void> {
    this.heartbeatInFlight = true;
    const permission = this.media.currentPermission;
    const body: DeviceHeartbeat = {
      registration_state: HEARTBEAT_STATE[this.phone.status],
      audio_permission: permission === 'granted' ? 'granted' : permission === 'denied' ? 'denied' : 'unknown',
      client_version: this.options.clientVersion,
    };
    try {
      const result = await this.options.api.heartbeat(this.deviceId, body);
      if (this.disposed) return;
      if (result.ok) {
        const changed = this.issues.delete('heartbeat_failing') || this.issues.delete('session_expired');
        if (changed) this.invalidate();
      } else if (result.kind === 'http' && result.status === 404) {
        await this.revoke('device_revoked');
        return;
      } else if (result.kind === 'http' && result.status === 401) {
        this.issues.add('session_expired');
        this.invalidate();
      } else {
        this.issues.add('heartbeat_failing');
        this.invalidate();
      }
    } finally {
      this.heartbeatInFlight = false;
      if (!this.revoked && this.running) this.scheduleHeartbeat(this.policy.heartbeatIntervalMs);
    }
  }

  // ---- Offers --------------------------------------------------------------------------------

  private acceptingOffers(status: PhoneStatus = this.phone.status): boolean {
    return this.running && !this.disposed && !this.revoked && this.registered && status === 'registered'
      && this.bootstrap !== null && this.scheduler.now() < this.bootstrap.expiresAt;
  }

  private onOffer(generation: number, offer: SipSession): void {
    const bootstrap = this.bootstrap;
    if (generation !== this.generation || !this.acceptingOffers() || !bootstrap) {
      this.refuseRaw(offer, 480, 'phone_not_ready', null);
      return;
    }
    const callHeader = offer.header(bootstrap.correlation.callIdHeader);
    const legHeader = offer.header(bootstrap.correlation.legIdHeader);
    const roleHeader = offer.header(bootstrap.correlation.legRoleHeader)?.trim().toLowerCase();
    if (callHeader === undefined) { this.refuseRaw(offer, 403, 'uncorrelated_offer', null); return; }
    const callId = callHeader.trim().toLowerCase();
    if (!isUuid(callId) || (legHeader !== undefined && !isUuid(legHeader.trim())) || (roleHeader !== 'source' && roleHeader !== 'target')) {
      this.refuseRaw(offer, 403, 'invalid_correlation', isUuid(callId) ? callId : null);
      return;
    }
    const existing = this.recordByCallId(callId);
    if (existing && existing.sip && legLive(existing.leg)) { this.refuseRaw(offer, 486, 'duplicate_offer', callId); return; }
    if ([...this.pendingOffers].some(p => p.callId === callId)) { this.refuseRaw(offer, 486, 'duplicate_offer', callId); return; }
    const isReofferedInbound = roleHeader === 'target' && existing?.origin === 'inbound' && existing.leg === 'terminated' && !existing.cancelRequested && existing.endReason !== 'declined';
    if (existing && !isReofferedInbound && (existing.cancelRequested || existing.leg !== 'none')) { this.refuseRaw(offer, 487, existing.cancelRequested ? 'call_cancelled' : 'call_finished', callId); return; }
    const liveLegs = this.liveRecords().filter(r => r.sip || r.origin === 'inbound').length;
    const ownOutbound = existing?.origin === 'outbound';
    if (!ownOutbound && liveLegs + this.pendingOffers.size >= this.policy.maxConcurrentCalls) { this.refuseRaw(offer, 486, 'busy', callId); return; }

    const pending: PendingOffer = { offer, callId, role: roleHeader, done: false };
    this.pendingOffers.add(pending);
    const stop = offer.onTerminated(() => { if (!pending.done) { pending.done = true; this.pendingOffers.delete(pending); } });
    void this.classifyOffer(pending).finally(stop);
  }

  private async classifyOffer(pending: PendingOffer): Promise<void> {
    // A fast PBX can deliver the INVITE before our POST /calls receipt. Wait (bounded) for in-flight creates.
    const inflight = [...this.calls.values()].filter(r => r.origin === 'outbound' && r.callId === null && r.createSettled && !r.localFailure);
    if (inflight.length) {
      for (const r of inflight) if (r.create?.phase === 'unconfirmed') void this.reconcileCreate(r);
      await withTimeout(this.scheduler, Promise.all(inflight.map(r => r.createSettled)), this.policy.offerVerificationTimeoutMs, null);
    }
    if (pending.done) return;
    if (!this.acceptingOffers() && !this.recordByCallId(pending.callId)) { this.refuse(pending, 480, 'phone_not_ready'); return; }

    const own = this.recordByCallId(pending.callId);
    if (own && own.origin === 'outbound') {
      if (pending.role !== 'source') { this.refuse(pending, 403, 'role_mismatch'); return; }
      if (own.cancelRequested || own.leg !== 'none' || own.localFailure) { this.refuse(pending, 487, own.cancelRequested ? 'call_cancelled' : 'call_finished'); return; }
      this.adoptOffer(pending, own);
      void this.autoAcceptOwn(own);
      return;
    }
    const isReofferedInbound = pending.role === 'target' && own?.origin === 'inbound' && own.leg === 'terminated' && !own.cancelRequested && own.endReason !== 'declined';
    if (own && !isReofferedInbound) { this.refuse(pending, 486, 'duplicate_offer'); return; }

    // Inbound: verify the server knows this call, the user may see it, and it is still live.
    if (pending.role !== 'target') { this.refuse(pending, 403, 'role_mismatch'); return; }
    const verified = await this.verifyInbound(pending.callId);
    if (pending.done) return;
    if (!verified.ok) { this.refuse(pending, verified.status, verified.reason); return; }
    const prior = this.recordByCallId(pending.callId);
    if (prior) {
      if (prior.origin === 'inbound' && prior.leg === 'terminated' && !prior.cancelRequested && prior.endReason !== 'declined') {
        this.removeRecord(prior);
      } else {
        this.refuse(pending, 486, 'duplicate_offer');
        return;
      }
    }
    const record = this.newRecord('inbound');
    record.callId = pending.callId;
    record.server = verified.call;
    this.byCallId.set(pending.callId, record.key);
    this.calls.set(record.key, record);
    this.adoptOffer(pending, record);
    if (!legLive(record.leg)) return;
    try { await pending.offer.progress(); } catch { /* 180 is advisory; the offer stays valid */ }
    if (!legLive(record.leg)) return;
    this.commit(record);
    this.emitter.emit({ type: 'incoming_call', call: this.callSnapshot(record) });
  }

  private async verifyInbound(callId: string): Promise<{ ok: true; call: Call } | { ok: false; status: number; reason: OfferRefusalReason }> {
    const timeout = Math.max(500, Math.floor(this.policy.offerVerificationTimeoutMs / 2));
    for (let attempt = 0; attempt < 2; attempt++) {
      const result = await withTimeout(this.scheduler, this.options.api.getCall(callId), timeout, null);
      if (result === null) continue;
      if (result.ok) {
        const call = result.value;
        if (call.id.toLowerCase() !== callId) return { ok: false, status: 403, reason: 'offer_not_authorized' };
        if (terminalCall(call)) return { ok: false, status: 487, reason: 'call_finished' };
        if (this.options.userId && !call.participants.some(p => p.user_id?.toLowerCase() === this.options.userId!.toLowerCase())) {
          return { ok: false, status: 403, reason: 'offer_not_for_this_user' };
        }
        return { ok: true, call };
      }
      if (result.kind === 'http' && result.status < 500) return { ok: false, status: 403, reason: 'offer_not_authorized' };
    }
    return { ok: false, status: 480, reason: 'offer_unverifiable' };
  }

  private adoptOffer(pending: PendingOffer, record: CallRecord): void {
    pending.done = true;
    this.pendingOffers.delete(pending);
    const offer = pending.offer;
    record.sip = offer;
    record.sipDisposers.push(
      offer.onTerminated(reason => this.onSipTerminated(record, reason)),
      offer.onMediaState(state => this.onMediaState(record, state)),
      offer.onRemoteStream(() => { if (record.leg === 'established') this.connectRemoteAudio(record); }),
    );
    if (offer.state === 'terminated') this.onSipTerminated(record, 'remote_cancel');
  }

  private refuse(pending: PendingOffer, status: number, reason: OfferRefusalReason): void {
    pending.done = true;
    this.pendingOffers.delete(pending);
    this.refuseRaw(pending.offer, status, reason, pending.callId);
  }

  private refuseRaw(offer: SipSession, status: number, reason: OfferRefusalReason, callId: string | null): void {
    if (offer.state === 'offered') void offer.reject(status).catch(() => {});
    this.log('warn', 'offer_refused', { reason, status, call_id: callId });
    this.emitter.emit({ type: 'offer_refused', reason, sip_status: status, call_id: callId });
  }

  // ---- Outbound -----------------------------------------------------------------------------

  private validateCreate(input: PlaceCallInput): Result<CreateCall> {
    if (typeof input !== 'object' || input === null) return fail('invalid_argument', 'Call input is required.');
    const keys = Object.keys(input);
    if (!keys.every(k => k === 'destination' || k === 'from_number_id' || k === 'contact_id' || k === 'calling_session')) return fail('invalid_argument', 'Unknown call request field.');
    const d = input.destination as unknown;
    if (typeof d !== 'object' || d === null) return fail('invalid_destination', 'Choose a teammate or an E.164 number.');
    const dest = d as Record<string, unknown>;
    let destination: Destination;
    if (dest.kind === 'internal' && Object.keys(dest).length === 2 && isUuid(dest.user_id)) destination = { kind: 'internal', user_id: dest.user_id.toLowerCase() };
    else if (dest.kind === 'pstn' && Object.keys(dest).length === 2 && isE164(dest.e164)) destination = { kind: 'pstn', e164: dest.e164 };
    else return fail('invalid_destination', 'Only a teammate or an E.164 number can be called; SIP URIs and dial strings are not accepted.');
    if (input.from_number_id !== undefined && !isUuid(input.from_number_id)) return fail('invalid_argument', 'from_number_id must be a number ID.');
    if (input.contact_id !== undefined && !isUuid(input.contact_id)) return fail('invalid_argument', 'contact_id must be a contact ID.');
    if (destination.kind === 'pstn' && input.from_number_id === undefined) return fail('invalid_argument', 'Choose the number to call from.');
    let callingSession: CallingSessionIntent | undefined;
    if (input.calling_session !== undefined) {
      if (destination.kind !== 'pstn') return fail('invalid_argument', 'calling_session is only supported for PSTN calls.');
      const cs = input.calling_session as unknown as Record<string, unknown>;
      if (typeof cs !== 'object' || cs === null || !isUuid(cs.attempt_id)) return fail('invalid_argument', 'calling_session.attempt_id must be a UUID.');
      if (cs.candidate_id !== undefined && (typeof cs.candidate_id !== 'number' || !Number.isSafeInteger(cs.candidate_id) || cs.candidate_id <= 0)) {
        return fail('invalid_argument', 'calling_session.candidate_id must be a positive integer.');
      }
      if (cs.controller_id !== undefined && cs.controller_id !== null && !isUuid(cs.controller_id)) {
        return fail('invalid_argument', 'calling_session.controller_id must be a UUID.');
      }
      callingSession = {
        attempt_id: cs.attempt_id.toLowerCase(),
        ...(cs.candidate_id !== undefined ? { candidate_id: cs.candidate_id } : {}),
        ...(typeof cs.controller_id === 'string' ? { controller_id: cs.controller_id.toLowerCase() } : cs.controller_id === null ? { controller_id: null } : {}),
      };
    }
    return ok({
      destination,
      device_id: this.deviceId,
      ...(input.from_number_id !== undefined ? { from_number_id: input.from_number_id.toLowerCase() } : {}),
      ...(input.contact_id !== undefined ? { contact_id: input.contact_id.toLowerCase() } : {}),
      ...(callingSession !== undefined ? { calling_session: callingSession } : {}),
    });
  }

  private capabilityGate(destination: Destination): Result<void> {
    const capabilities = this.options.capabilities?.() ?? null;
    const name = destination.kind === 'pstn' ? 'calling.pstn.outbound' : 'calling.internal';
    const capability = capabilities?.[name];
    if (!capability) {
      return destination.kind === 'pstn' ? fail('pstn_not_enabled', 'Outside calling is not enabled for this workspace.') : okVoid;
    }
    if (capability.status !== 'enabled' || !capability.allowed) {
      return destination.kind === 'pstn'
        ? fail('pstn_not_enabled', capability.detail ?? 'Outside calling is not enabled for this workspace.', false, capability.reason_code ?? undefined)
        : fail('capability_unavailable', capability.detail ?? 'Calling is not available.', false, capability.reason_code ?? undefined);
    }
    return okVoid;
  }

  private async sendCreate(record: CallRecord): Promise<Result<CommandView>> {
    const key = record.createKey!, body = record.request!;
    const { view, value } = await this.commands.send(key, 'create', null, async () => {
      let result = await this.options.api.createCall(body, key);
      if (!result.ok && result.kind === 'http' && result.code === 'device_not_ready' && !record.cancelRequested) {
        this.registered = false;
        await withTimeout(this.scheduler, this.drive('retry_now'), 8000, undefined);
        if (this.acceptingOffers() && !record.cancelRequested) result = await this.options.api.createCall(body, key);
      }
      return result;
    }, v => { record.create = v; this.commit(record); });
    if (this.disposed) return fail('engine_disposed', 'The call engine was disposed.');
    if (!value) {
      record.create = view;
      if (view.phase === 'unconfirmed') {
        record.error = engineError('command_unconfirmed', 'The phone system has not confirmed the call request yet.', true);
        this.scheduleCreateReconcile(record, 0);
        this.commit(record);
        return fail('command_unconfirmed', 'The call request was not confirmed. It will be checked with the same request key.', true);
      }
      record.localFailure = true;
      record.error = engineError('server_rejected', view.error?.message ?? 'The call could not be started.', false, view.error?.code);
      this.terminateLocalLeg(record, 'create_rejected');
      return { ok: false, error: record.error };
    }
    this.onCreateReceipt(record, view, value);
    return ok(record.create ?? view);
  }

  private onCreateReceipt(record: CallRecord, view: CommandView, receipt: CommandReceipt): void {
    record.timers.clear('create_reconcile');
    record.callId = receipt.call.id.toLowerCase();
    this.byCallId.set(record.callId, record.key);
    record.error = null;
    record.create = this.commands.track(record.callId, view, receipt);
    if (record.cancelRequested) void this.serverEndAction(record, ['hangup']);
    this.commit(record);
  }

  private scheduleCreateReconcile(record: CallRecord, attempt: number): void {
    const delays = this.policy.createReconcileDelaysMs;
    if (attempt >= delays.length) return;
    record.timers.set('create_reconcile', delays[attempt]!, () => { void this.reconcileCreate(record).then(done => { if (!done) this.scheduleCreateReconcile(record, attempt + 1); }); });
  }

  /** Re-sends `POST /calls` with the SAME idempotency key to learn whether the server created the call. */
  private async reconcileCreate(record: CallRecord): Promise<boolean> {
    if (record.callId || record.localFailure || this.disposed || !record.createKey || !record.request) return true;
    const key = record.createKey, body = record.request;
    const result = await this.options.api.createCall(body, key);
    if (record.callId || this.disposed) return true;
    if (result.ok) { this.onCreateReceipt(record, record.create!, result.value); return true; }
    if (result.kind === 'http' && result.status < 500) {
      record.localFailure = true;
      record.error = engineError('server_rejected', result.message, false, result.code);
      this.terminateLocalLeg(record, 'create_rejected');
      return true;
    }
    return false;
  }

  private async autoAcceptOwn(record: CallRecord): Promise<void> {
    const track = record.localStream?.getAudioTracks()[0];
    if (!track || track.readyState !== 'live') {
      // The pre-acquired microphone ended while waiting (unplug/revocation): reopen the default before answering.
      const mic = await this.media.acquire();
      if (!mic.ok) { record.error = mic.error; this.commit(record); void this.hangup(record.key); return; }
      if (record.leg !== 'none' || record.cancelRequested) { stopStream(mic.value.stream); return; }
      this.setLocalStream(record, mic.value.stream);
    }
    if (!this.transitionLeg(record, 'accepting')) return;
    this.commit(record);
    record.acceptPromise = this.acceptSip(record);
    void record.acceptPromise.finally(() => { record.acceptPromise = null; });
  }

  // ---- Inbound accept / decline ----------------------------------------------------------

  private async acceptInbound(record: CallRecord, holdOthers: boolean): Promise<Result<void>> {
    if (holdOthers) {
      for (const other of this.liveRecords()) {
        if (other === record || other.leg !== 'established' || other.server?.state !== 'active') continue;
        if (other.server.allowed_actions.includes('hold')) await this.serverAction(other.key, 'hold');
      }
    }
    const mic = await this.media.acquire();
    if (!mic.ok) { record.error = mic.error; this.commit(record); return { ok: false, error: mic.error }; }
    if (record.leg !== 'offered' || !record.sip || record.sip.state !== 'offered') {
      stopStream(mic.value.stream);
      return fail('call_ended', 'The caller is no longer waiting.');
    }
    record.error = null;
    this.setLocalStream(record, mic.value.stream);
    this.transitionLeg(record, 'accepting');
    this.commit(record);
    return this.acceptSip(record);
  }

  private async acceptSip(record: CallRecord): Promise<Result<void>> {
    const sip = record.sip, stream = record.localStream;
    if (!sip || !stream) { this.terminateLocalLeg(record, 'answer_failed'); return fail('answer_failed', 'The call could not be answered.'); }
    try {
      await sip.accept(stream);
    } catch {
      if (legLive(record.leg)) { record.error = engineError('answer_failed', 'The call could not be answered.', true); this.terminateLocalLeg(record, 'answer_failed'); }
      return fail(record.endReason === 'remote_cancel' ? 'call_ended' : 'answer_failed', 'The call could not be answered.');
    }
    if (record.wantBye || record.cancelRequested || terminalCall(record.server)) {
      // Cancellation closes local tracks while accept is in flight. A late
      // established dialog still needs exactly one BYE.
      await this.byeNow(record);
      return fail('call_ended', 'The call ended while it was being answered.');
    }
    // SIP.js can resolve accept() after a racing CANCEL; trust the dialog state, not the promise.
    if (sip.state !== 'established' || record.leg !== 'accepting') {
      if (legLive(record.leg) && record.leg !== 'terminating') this.terminateLocalLeg(record, 'remote_cancel');
      return fail('call_ended', 'The call ended before it connected.');
    }
    this.transitionLeg(record, 'established');
    record.media = record.media === 'none' ? 'connecting' : record.media;
    // Hangup or server end arrived while the 200 OK was in flight: leave immediately.
    if (record.wantBye || record.cancelRequested || terminalCall(record.server)) {
      this.silence(record);
      await this.byeNow(record);
      return fail('call_ended', 'The call ended while it was being answered.');
    }
    this.connectRemoteAudio(record);
    this.commit(record);
    return okVoid;
  }

  private async declineOffer(record: CallRecord): Promise<Result<void>> {
    this.transitionLeg(record, 'terminating');
    record.endReason = 'declined';
    this.commit(record);
    record.cancelRequested = true;
    // Stop tracks and close the local leg before any network await. SIP and the
    // audited server intent continue independently, even through a lost response.
    const intent = this.serverEndAction(record, ['reject', 'hangup']);
    this.terminateLocalLeg(record, 'declined', 603);
    void intent.catch(() => {});
    return okVoid;
  }

  // ---- Ending ------------------------------------------------------------------------------------

  private async endCall(record: CallRecord, reason: string): Promise<Result<void>> {
    record.endReason ??= reason;
    record.cancelRequested = true;
    record.wantBye = true;
    // Evidence before the peer closes. Media-loss cleanup waits (bounded) so the server sees the report
    // before the hangup command and can label the end as automatic; a user hangup is never delayed.
    const snapshot = this.diagnoseTeardown(record);
    if (record.endReason === 'media_lost') await withTimeout(this.scheduler, snapshot, this.policy.serverActionBeforeSipMs, false);
    if (record.origin === 'outbound' && !record.callId) {
      // Request still in flight or unconfirmed: cancellation dominates. Reconcile with the same key, then hang up.
      record.cancelRequested = true;
      this.terminateLocalLeg(record, reason);
      if (record.create?.phase === 'unconfirmed') void this.reconcileCreate(record);
      return okVoid;
    }
    // Invoke the server intent first for provenance, but never await it to stop
    // browser media. This also fences a late 200 OK via wantBye/cancelRequested.
    const intent = this.serverEndAction(record, ['hangup']);
    this.terminateLocalLeg(record, reason);
    void intent.catch(() => {});
    return okVoid;
  }

  private async byeNow(record: CallRecord): Promise<void> {
    if (record.byeSent) return;
    record.byeSent = true;
    if (record.leg === 'established') this.transitionLeg(record, 'terminating');
    const sip = record.sip;
    if (sip && sip.state === 'established') { try { await sip.bye(); } catch { /* transport may be down */ } }
    if (legLive(record.leg)) this.onSipTerminated(record, 'local_bye');
  }

  /** Server-first end: bounded wait so the server records reject/hangup intent before the leg drops. */
  private async serverEndAction(record: CallRecord, preferred: readonly ('reject' | 'hangup')[]): Promise<void> {
    if (!record.callId) return;
    if (!record.server) await withTimeout(this.scheduler, this.refreshCall(record), this.policy.serverActionBeforeSipMs, undefined);
    let server = record.server;
    if (!server || terminalCall(server)) return;
    // End intent remains valid even if an older projection hid controls during a drop.
    let action = preferred.find(a => server!.allowed_actions.includes(a)) ?? preferred[0];
    if (!action) return;
    const initialVersion = server.version;
    const run = this.commands.run(record.callId, { action, expected_version: initialVersion }, `${record.callId}:end`);
    const first = await withTimeout(this.scheduler, run, this.policy.serverActionBeforeSipMs, null);
    if (first?.phase === 'conflict' && record.callId) {
      await withTimeout(this.scheduler, this.refreshCall(record), this.policy.serverActionBeforeSipMs, undefined);
      server = record.server;
      if (!server || terminalCall(server) || server.version === initialVersion) return;
      action = preferred.find(a => server!.allowed_actions.includes(a)) ?? preferred[0];
      if (!action) return;
      const retry = this.commands.run(record.callId, { action, expected_version: server.version }, `${record.callId}:end:retry`);
      await withTimeout(this.scheduler, retry, this.policy.serverActionBeforeSipMs, null);
    }
  }

  /** Revocation/dispose path: local leg only, no server action. */
  private async terminateLocally(record: CallRecord, reason: string): Promise<void> {
    record.endReason ??= reason;
    this.silence(record);
    const sip = record.sip;
    if (sip?.state === 'offered') { try { await sip.reject(480); } catch { /* ignore */ } }
    else if (sip?.state === 'established' && !record.byeSent) { record.byeSent = true; try { await sip.bye(); } catch { /* ignore */ } }
    if (legLive(record.leg)) this.onSipTerminated(record, 'transport_stopped');
  }

  private terminateLocalLeg(record: CallRecord, reason: string, rejectCode = 487): void {
    record.endReason ??= reason;
    const sip = record.sip;
    if (sip?.state === 'offered') void sip.reject(rejectCode).catch(() => {});
    else if (sip?.state === 'established' && !record.byeSent) { record.byeSent = true; void sip.bye().catch(() => {}); }
    if (legLive(record.leg)) this.onSipTerminated(record, 'local_bye');
    else this.commit(record);
  }

  private onSipTerminated(record: CallRecord, reason: SipTerminationReason): void {
    if (!legLive(record.leg)) return;
    record.endReason ??= reason;
    void this.diagnoseTeardown(record);
    this.transitionLeg(record, 'terminated');
    this.cleanupMedia(record);
    for (const dispose of record.sipDisposers.splice(0)) { try { dispose(); } catch { /* ignore */ } }
    record.timers.clear('media_drop');
    record.timers.clear('ice_restart');
    if (record.callId && !terminalCall(record.server)) {
      void this.refreshCall(record);
      record.timers.set('confirm', this.policy.endingConfirmTimeoutMs, () => { void this.refreshCall(record); });
    }
    this.commit(record);
    this.maybeFinishDeferredTeardown();
    this.prune();
  }

  // ---- Server state ------------------------------------------------------------------------------

  private applyServer(record: CallRecord, call: Call): void {
    if (record.callId && call.id.toLowerCase() !== record.callId) return;
    const previous = record.server;
    if (previous && call.version < previous.version) return;
    record.server = call;
    if (previous && participantsKey(previous) !== participantsKey(call)) record.capture?.participantsChanged();
    record.capture?.mark('held', call.state === 'held');
    if (terminalCall(call)) {
      record.timers.clear('confirm');
      this.commands.cancel(record.callId ?? call.id);
      // Server state wins: a call the server ended must not keep a local leg or microphone open.
      if (legLive(record.leg)) {
        record.endReason ??= `server_${call.state}`;
        if (record.leg === 'accepting') record.wantBye = true;
        else this.terminateLocalLeg(record, `server_${call.state}`);
      }
      void record.capture?.stop('call_ended');
    }
    this.commit(record);
    this.prune();
  }

  private applyCommand(record: CallRecord, view: CommandView): void {
    if (view.kind === 'create') {
      record.create = view;
      if (view.phase === 'failed' && !terminalCall(record.server)) {
        record.localFailure = true;
        record.error = engineError('server_rejected', 'The phone system could not start the call.', false, view.error?.code);
        if (legLive(record.leg)) this.terminateLocalLeg(record, 'create_failed');
      }
    } else {
      const index = record.commands.findIndex(c => c.idempotency_key === view.idempotency_key);
      if (index >= 0) record.commands[index] = view; else record.commands.push(view);
      if (record.commands.length > MAX_COMMAND_HISTORY) record.commands.splice(0, record.commands.length - MAX_COMMAND_HISTORY);
    }
    this.commit(record);
  }

  private refreshCall(record: CallRecord): Promise<void> {
    if (!record.callId || this.disposed) return Promise.resolve();
    if (record.refreshing) return record.refreshing;
    const task = (async () => {
      try {
        const result = await this.options.api.getCall(record.callId!);
        if (result.ok) this.applyServer(record, result.value);
      } finally {
        record.refreshing = null;
      }
    })();
    record.refreshing = task;
    return task;
  }

  private async serverAction(key: string, action: 'hold' | 'resume'): Promise<Result<CommandView>> {
    const record = this.calls.get(key);
    if (!record) return fail('call_not_found', 'That call is no longer available.');
    if (record.leg !== 'established' || !record.callId || !record.server) return fail('invalid_call_state', 'The call is not connected.');
    if (!record.server.allowed_actions.includes(action)) return fail('action_not_allowed', `${action === 'hold' ? 'Hold' : 'Resume'} is not allowed on this call now.`);
    const view = await this.commands.run(record.callId, { action, expected_version: record.server.version }, `${record.callId}:${action}`);
    const result = this.viewResult(view);
    return result.ok ? ok(view) : result;
  }

  private viewResult(view: CommandView): Result<CommandView> {
    switch (view.phase) {
      case 'conflict': return fail('server_conflict', 'The call changed. Review it and try again.', true, view.error?.code);
      case 'rejected':
      case 'failed': return fail('server_rejected', view.error?.message ?? 'The phone system did not accept the request.', false, view.error?.code);
      case 'unconfirmed': return fail('command_unconfirmed', 'Not confirmed. Checking...', true);
      default: return ok(view);
    }
  }

  // ---- Media -------------------------------------------------------------------------------------

  private setLocalStream(record: CallRecord, stream: MediaStreamLike): void {
    const previous = record.localStream;
    record.trackDisposer?.();
    record.localStream = stream;
    const track = stream.getAudioTracks()[0];
    if (track) {
      const onEnded = () => { void this.recoverMicrophone(record); };
      track.addEventListener('ended', onEnded);
      record.trackDisposer = () => track.removeEventListener('ended', onEnded);
    } else {
      record.trackDisposer = null;
    }
    this.applyMute(record);
    if (previous && previous !== stream) stopStream(previous);
    const announced = record.localTrack;
    if ((track ?? null) !== announced) {
      record.localTrack = track ?? null;
      this.announceTracks(record, announced ? 'local_replaced' : 'local_attached');
    }
  }

  private announceTracks(record: CallRecord, cause: TrackChangeCause): void {
    this.emitter.emit({ type: 'tracks_changed', key: record.key, call_id: record.callId, cause, local: record.localTrack, remote: record.remoteTrack });
  }

  /** Follows the remote audio track that the sink plays, including replacement and end. */
  private watchRemoteTrack(record: CallRecord, stream: MediaStreamLike): void {
    const tracks = stream.getAudioTracks();
    const track = tracks.find(t => t.readyState === 'live') ?? tracks[0] ?? null;
    if (track === record.remoteTrack) return;
    const previous = record.remoteTrack;
    record.remoteTrackDisposer?.();
    record.remoteTrackDisposer = null;
    record.remoteTrack = track;
    if (track) {
      const onEnded = () => {
        if (record.remoteTrack !== track) return;
        record.remoteTrackDisposer?.();
        record.remoteTrackDisposer = null;
        record.remoteTrack = null;
        this.announceTracks(record, 'remote_ended');
      };
      track.addEventListener('ended', onEnded);
      record.remoteTrackDisposer = () => track.removeEventListener('ended', onEnded);
    }
    this.announceTracks(record, track ? (previous ? 'remote_replaced' : 'remote_attached') : 'remote_ended');
  }

  private applyMute(record: CallRecord): void {
    const track = record.localStream?.getAudioTracks()[0];
    if (!track) { record.microphone = 'none'; return; }
    track.enabled = !record.muted && record.leg !== 'terminating';
    record.microphone = track.readyState !== 'live' ? 'lost' : record.muted ? 'muted' : 'live';
  }

  private silence(record: CallRecord): void {
    const track = record.localStream?.getAudioTracks()[0];
    if (track) track.enabled = false;
  }

  private async swapLocalStream(record: CallRecord, stream: MediaStreamLike): Promise<Result<void>> {
    const track = stream.getAudioTracks()[0] as MediaStreamTrackLike;
    track.enabled = !record.muted;
    if (record.sip && (record.leg === 'established' || record.leg === 'accepting')) {
      try { await record.sip.replaceLocalTrack(track); } catch {
        stopStream(stream);
        return fail('input_device_unavailable', 'The microphone could not be switched.', true);
      }
    }
    if (!legLive(record.leg)) { stopStream(stream); return fail('call_ended', 'The call has ended.'); }
    this.setLocalStream(record, stream);
    record.capture?.replaceLocal(stream);
    this.commit(record);
    return okVoid;
  }

  /** Track ended (unplug, OS revocation): fall back to the system default; if none, the call stays up with the microphone lost. */
  private async recoverMicrophone(record: CallRecord): Promise<void> {
    if (record.recovering || !legLive(record.leg) || record.leg === 'terminating') return;
    const track = record.localStream?.getAudioTracks()[0];
    if (track && track.readyState === 'live') return;
    record.recovering = true;
    try {
      record.microphone = 'lost';
      this.commit(record);
      if (this.media.currentPermission === 'denied') return;
      const acquired = await this.media.acquire(null);
      if (!acquired.ok) { record.error = acquired.error; this.commit(record); return; }
      if (this.ending(record)) { stopStream(acquired.value.stream); return; }
      const swapped = await this.swapLocalStream(record, acquired.value.stream);
      if (swapped.ok) this.emitter.emit({ type: 'media', event: { type: 'input_fallback', from: track?.getSettings().deviceId ?? null, reason: 'track_ended' } });
    } finally {
      record.recovering = false;
    }
  }

  private connectRemoteAudio(record: CallRecord): void {
    const stream = record.sip?.remoteStream() ?? null;
    if (!stream) return;
    if (!record.sink) { record.sink = this.options.media.createAudioSink(); record.externalSink = false; }
    record.sink.setStream(stream);
    record.remoteAudio = 'attached';
    this.watchRemoteTrack(record, stream);
    const output = this.media.output;
    if (output && this.media.outputSelectionSupported) void record.sink.setOutput(output).catch(() => {});
    void this.play(record);
  }

  private async play(record: CallRecord): Promise<void> {
    const sink = record.sink;
    if (!sink) return;
    try {
      await sink.play();
      if (record.sink !== sink || !legLive(record.leg)) return;
      record.remoteAudio = 'playing';
      this.media.setPlayback('playing');
    } catch {
      if (record.sink !== sink || !legLive(record.leg)) return;
      record.remoteAudio = 'blocked';
      this.media.setPlayback('blocked');
    }
    this.commit(record);
  }

  /** One bounded report; stats are read from the live peer connection first and reduced to the allowlist. */
  private async diagnose(record: CallRecord, kind: DiagnosticReportKind, endReason?: string | null): Promise<boolean> {
    const reporter = this.diagnostics, callId = record.callId;
    if (!reporter || !callId) return false;
    let metrics = {};
    try { metrics = metricsFromStats(await record.sip?.diagnosticStats?.() ?? null); } catch { /* stats are optional evidence */ }
    const signaling = record.signaling === 'degraded' ? 'degraded' : 'live';
    const reconnects = record.reconnects > 0 ? { reconnects: record.reconnects } : {};
    return reporter.send(callId, kind, { end_reason: endReason ?? null, metrics: { ...metrics, signaling_state: signaling, ...reconnects } });
  }
  private diagnoseTeardown(record: CallRecord): Promise<boolean> {
    if (record.diagTeardown) return Promise.resolve(false);
    record.diagTeardown = true;
    return this.diagnose(record, 'teardown', record.endReason).catch(() => false);
  }
  private diagnoseMedia(record: CallRecord, kind: DiagnosticReportKind): void {
    if (record.diagMedia === kind) return;
    record.diagMedia = kind;
    void this.diagnose(record, kind);
  }

  private onMediaState(record: CallRecord, state: SipMediaState): void {
    if (!legLive(record.leg)) return;
    switch (state) {
      case 'connected':
        this.diagnoseMedia(record, record.diagConnected ? 'media_recovered' : 'call_connected');
        record.diagConnected = true;
        record.media = 'connected';
        record.timers.clear('media_drop');
        record.timers.clear('ice_restart');
        record.capture?.mark('media_lost', false);
        break;
      case 'new':
      case 'connecting':
        if (record.media !== 'recovering') record.media = 'connecting';
        break;
      case 'disconnected':
        this.diagnoseMedia(record, 'media_recovering');
        record.media = 'recovering';
        if (this.policy.iceRestartOnDisconnect && record.sip) {
          const sip = record.sip;
          record.timers.set('ice_restart', 2000, () => { void sip.restartIce().catch(() => {}); });
        }
        if (!record.timers.has('media_drop')) record.timers.set('media_drop', this.policy.mediaLossDropMs, () => this.mediaDropped(record));
        record.capture?.mark('media_lost', true);
        break;
      case 'failed':
        this.diagnoseMedia(record, 'media_lost');
        record.media = 'lost';
        if (!record.timers.has('media_drop')) record.timers.set('media_drop', this.policy.mediaLossDropMs, () => this.mediaDropped(record));
        record.capture?.mark('media_lost', true);
        break;
      case 'closed':
        return;
    }
    this.commit(record);
  }

  private mediaDropped(record: CallRecord): void {
    if (record.media === 'connected' || record.leg !== 'established') return;
    record.endReason = 'media_lost';
    void this.hangup(record.key);
  }

  private cleanupMedia(record: CallRecord): void {
    record.trackDisposer?.();
    record.trackDisposer = null;
    record.remoteTrackDisposer?.();
    record.remoteTrackDisposer = null;
    const hadTracks = record.localTrack !== null || record.remoteTrack !== null;
    record.localTrack = null;
    record.remoteTrack = null;
    if (hadTracks) this.announceTracks(record, 'call_ended');
    stopStream(record.localStream);
    record.localStream = null;
    if (!this.running && ![...this.calls.values()].some(r => r !== record && r.localStream)) this.media.releaseRetained();
    record.microphone = 'none';
    if (record.sink) {
      record.sink.setStream(null);
      if (!record.externalSink) record.sink.dispose();
      record.sink = null;
    }
    record.remoteAudio = 'none';
    if (record.media !== 'none') record.media = 'none';
    if (record.capture) void record.capture.stop('call_ended');
    if (![...this.calls.values()].some(r => r !== record && r.remoteAudio === 'playing')) this.media.setPlayback('idle');
  }

  // ---- Capture ------------------------------------------------------------------------------------

  private captureAvailability(record: CallRecord): CaptureAvailability {
    const backend = this.options.captureBackend;
    if (!backend) return { status: 'unsupported', reason: 'no_backend' };
    if (!backend.supported) return { status: 'unsupported', reason: 'platform_unsupported' };
    const gate = captureCapabilityStatus(this.options.capabilities?.()?.[CAPTURE_CAPABILITY]);
    if (!gate.ok) return { status: 'unsupported', reason: gate.reason };
    if (record.capture && record.capture.state === 'recording') return { status: 'active' };
    if (record.leg !== 'established') return { status: 'unavailable', reason: 'call_not_established' };
    if (record.microphone === 'lost' || !record.localStream) return { status: 'unavailable', reason: 'microphone_lost' };
    if (!record.sip?.remoteStream()) return { status: 'unavailable', reason: 'no_remote_audio' };
    return { status: 'available' };
  }

  private async startCapture(key: string, consent: LocalCaptureConsent): Promise<Result<LocalCaptureHandle>> {
    const record = this.calls.get(key);
    if (!record) return fail('call_not_found', 'That call is no longer available.');
    if (!validCaptureConsent(consent, this.scheduler.now())) return fail('consent_required', 'Explicit, recent consent is required before local capture.');
    const availability = this.captureAvailability(record);
    if (availability.status === 'unsupported') return fail('capture_unsupported', `Local capture is not supported (${availability.reason}).`, false, availability.reason);
    if (availability.status !== 'available') return fail('capture_unavailable', 'Local capture is not available on this call now.', true);
    const backend = this.options.captureBackend!;
    const session = new LocalCaptureSession(record.callId!, consent, this.scheduler, this.options.captureLimits ?? DEFAULT_CAPTURE_LIMITS, s => {
      if (record.capture === s) { record.capture = null; this.commit(record); }
    });
    record.capture = session;
    try {
      const recorder = await backend.start({
        local: record.localStream!,
        remote: record.sip!.remoteStream()!,
        timesliceMs: (this.options.captureLimits ?? DEFAULT_CAPTURE_LIMITS).timesliceMs,
        onData: data => session.push(data),
        onError: () => session.recorderError(),
      });
      session.attach(recorder);
    } catch {
      record.capture = null;
      this.commit(record);
      return fail('capture_failed', 'Local capture could not start.', true);
    }
    if (!legLive(record.leg) || record.leg !== 'established') { await session.stop('call_ended'); return fail('call_ended', 'The call ended.'); }
    if (record.muted) session.mark('muted', true);
    if (record.server?.state === 'held') session.mark('held', true);
    this.commit(record);
    return ok(session);
  }

  // ---- Records and snapshots ----------------------------------------------------------------------

  /** Outbound records start at `none` (awaiting the server-originated leg); inbound records start at `offered`. */
  private newRecord(origin: 'outbound' | 'inbound'): CallRecord {
    return {
      key: this.uuid(), origin, createdAt: this.scheduler.now(), callId: null, server: null, leg: origin === 'inbound' ? 'offered' : 'none', sip: null, sipDisposers: [],
      localStream: null, trackDisposer: null, sink: null, externalSink: false, remoteAudio: 'none', muted: false, microphone: 'none',
      media: 'none', signaling: 'live', request: null, createKey: null, create: null, createSettled: null, commands: [],
      cancelRequested: false, wantBye: false, byeSent: false, localFailure: false, endReason: null, error: null, acceptPromise: null, endPromise: null,
      dtmfChain: Promise.resolve(), lastPhase: null, timers: new TimerSet(this.scheduler), capture: null, recovering: false, refreshing: null,
      localTrack: null, remoteTrack: null, remoteTrackDisposer: null, diagConnected: false, diagMedia: null, diagTeardown: false, reconnects: 0,
    };
  }

  /** Reads the leg after an await (defeats stale control-flow narrowing). */
  private legOf(record: CallRecord): LegState { return record.leg; }
  private ending(record: CallRecord): boolean { return record.leg === 'terminating' || record.leg === 'terminated'; }

  private transitionLeg(record: CallRecord, to: LegState): boolean {
    if (record.leg === to) return true;
    if (!canTransitionLeg(record.leg, to)) {
      this.log('debug', 'leg_transition_ignored', { from: record.leg, to, call_id: record.callId });
      return false;
    }
    record.leg = to;
    this.applyMute(record);
    return true;
  }

  private recordByCallId(callId: string): CallRecord | null {
    const key = this.byCallId.get(callId.toLowerCase());
    return key ? this.calls.get(key) ?? null : null;
  }

  private liveRecords(): CallRecord[] {
    return [...this.calls.values()].filter(r => legLive(r.leg) && !(r.leg === 'none' && (r.localFailure || r.cancelRequested)));
  }

  private removeRecord(record: CallRecord): void {
    this.cleanupMedia(record);
    record.timers.clearAll();
    if (record.callId) { this.commands.cancel(record.callId); this.byCallId.delete(record.callId); }
    this.calls.delete(record.key);
    this.invalidate();
    this.emitter.emit({ type: 'call_removed', key: record.key });
  }

  private prune(): void {
    const finished = [...this.calls.values()].filter(r => !legLive(r.leg) && (terminalCall(r.server) || r.localFailure));
    const excess = finished.length - this.policy.retainEndedCalls;
    if (excess > 0) for (const record of finished.sort((a, b) => a.createdAt - b.createdAt).slice(0, excess)) this.removeRecord(record);
  }

  private phaseOf(record: CallRecord): CallPhase {
    return derivePhase({ origin: record.origin, leg: record.leg, server: record.server, create: record.create, cancelRequested: record.cancelRequested, localFailure: record.localFailure });
  }

  private callSnapshot(record: CallRecord): CallSnapshot {
    return Object.freeze({
      key: record.key,
      call_id: record.callId,
      origin: record.origin,
      phase: this.phaseOf(record),
      leg: record.leg,
      server: record.server,
      create: record.create,
      commands: Object.freeze([...record.commands]),
      muted: record.muted,
      held: record.server?.state === 'held',
      microphone: record.microphone,
      media: record.media,
      signaling: record.signaling,
      remote_audio: record.remoteAudio,
      end_reason: record.endReason,
      error: record.error,
      created_at: new Date(record.createdAt).toISOString(),
      capture: this.captureAvailability(record),
    });
  }

  private buildSnapshot(): EngineSnapshot {
    this.revision += 1;
    return Object.freeze({
      revision: this.revision,
      phone: this.phone,
      media: this.media.snapshot(),
      calls: Object.freeze([...this.calls.values()].sort((a, b) => a.createdAt - b.createdAt).map(r => this.callSnapshot(r))),
    });
  }

  private commit(record: CallRecord): void {
    this.invalidate();
    const phase = this.phaseOf(record);
    if (phase !== record.lastPhase) {
      record.lastPhase = phase;
      if (this.calls.has(record.key)) this.emitter.emit({ type: 'call_changed', call: this.callSnapshot(record) });
    }
  }

  private invalidate(): void {
    const bootstrap = this.bootstrap ? this.bootstrap.toJSON() : this.bootstrapState;
    if (this.phone.accepting_calls !== this.acceptingOffers() || this.phone.issues.join() !== [...this.issues].join() || this.phone.bootstrap !== bootstrap || this.phone.transport !== this.transportDiag.snapshot()) {
      this.phone = this.phoneSnapshot(this.phone.status, this.phone.reason, this.phone);
    }
    this.store.invalidate();
  }

  private setBootstrapState(status: BootstrapStatus, reason_code: string): void {
    this.bootstrapState = { status, reason_code };
  }

  private phoneSnapshot(status: PhoneStatus, reason: string | null, base?: Partial<PhoneSnapshot>): PhoneSnapshot {
    return Object.freeze({
      status,
      reason,
      device_id: this.deviceId,
      attempt: status === 'reconnecting' || status === 'offline' ? this.attempts : 0,
      next_retry_at: status === 'reconnecting' || status === 'offline' ? base?.next_retry_at ?? null : null,
      registered_at: status === 'registered' ? base?.registered_at ?? this.phone?.registered_at ?? null : null,
      bootstrap: this.bootstrap ? this.bootstrap.toJSON() : this.bootstrapState,
      issues: Object.freeze([...this.issues]),
      accepting_calls: this.acceptingOffers(status),
      transport: this.transportDiag.snapshot(),
    });
  }

  private setPhone(status: PhoneStatus, reason: string | null, extra: Partial<PhoneSnapshot> = {}): void {
    const previous = this.phone;
    this.phone = this.phoneSnapshot(status, reason, { ...previous, ...extra });
    this.store.invalidate();
    if (previous.status !== status || previous.reason !== reason || previous.attempt !== this.phone.attempt) {
      this.emitter.emit({ type: 'phone_changed', phone: this.phone });
      if (previous.status !== status && this.timers.has('heartbeat') && !this.heartbeatInFlight) this.scheduleHeartbeat(0);
    }
  }

  private log(level: 'debug' | 'warn', message: string, fields: Record<string, string | number | boolean | null> = {}): void {
    try { this.options.logger?.[level]?.(message, fields); } catch { /* logger isolated */ }
  }
}

function participantsKey(call: Call): string {
  return call.participants.map(p => `${p.id}:${p.left_at === null ? 'in' : 'out'}`).sort().join(',');
}
